import { CANVAS_VIDEO_MODULES } from "@/views/space/config/moduleActionWhitelist";
import { getCanvasQwenFreePerspectiveConsumePower } from "@xmiles/holopix-helper";
import { ModuleId, type ModuleIds } from "@xmiles/holopix-types";
import {
  CANVAS_DRAFT_FAMILY_IDS,
  EXPLORE_FAMILY_IDS,
  MODULE_ID_CANVAS_DRAFT_A3,
  MODULE_ID_EXPLORE_A3
} from "@/constants/a3ModuleIds";

export {
  CANVAS_DRAFT_FAMILY_IDS,
  EXPLORE_FAMILY_IDS,
  MODULE_ID_CANVAS_DRAFT_A3,
  MODULE_ID_EXPLORE_A3
} from "@/constants/a3ModuleIds";

export const AB_TEST_TYPES = {
  A: "a",
  B: "b"
};

// 临时屏蔽作品分享功能
export const DISABLED_BOARD_SHARE = !true;


// export const TASKCENTER_TIME = "2025-07-18 00:00:00";

/**
 * 埋点模型选择,不跟某个view层的数据映射挂钩
 */
export const styleModelOptions = [
  { label: "风格模型", value: "LoRA" },
  { label: "组合风格", value: "Style" },
  { label: "风格模型", value: "Controller" },
  { label: "视频模型", value: "Video" }
];

/**
 * 可发布作品白名单
 * 一键成稿 丰富细节 局部细化 线稿提取 相似图裂变 涂抹替换 智能扩图 表情生成
 */
export const PUBLISH_ARTWORK_MODULE_ID_WHITELIST: ModuleIds[] = [
  ModuleId.Explore,
  MODULE_ID_EXPLORE_A3 as ModuleIds,
  ModuleId.Colour,
  ModuleId.Refine,
  ModuleId.Sketch,
  ModuleId.Fission,
  ModuleId.Replace,
  ModuleId.Expansion,
  ModuleId.Expression
];

export function isPublishArtworkModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  const id = Number(moduleId);
  if (!Number.isFinite(id) || id <= 0) return false;
  return PUBLISH_ARTWORK_MODULE_ID_WHITELIST.includes(id as ModuleIds);
}

export const MODULE_ID_GPT_IMAGE2_ENTERPRISE = ModuleId.GptImage2Enterprise;
export const MODULE_ID_CANVAS_DRAFT = ModuleId.CanvasDraft;
/** 自由视角付费算力：15 算力/次 */
export const CANVAS_QWEN_FREE_PERSPECTIVE_CONSUME_POWER = getCanvasQwenFreePerspectiveConsumePower();

/**
 * 自由画布可发布模块白名单
 * 全能编辑V2/V1/V3 极速编辑Lite 万能渲染 智能模型(单/多/PSD) 一键成稿 相似图裂变 企业专线 局部修改 视频生成
 */
export const CANVAS_PUBLISH_MODULE_ID_WHITELIST: number[] = DISABLED_BOARD_SHARE
  ? []
  : [
      ModuleId.MonkeyV21,
      ModuleId.Monkey,
      ModuleId.SeedDream,
      ModuleId.KleinFast,
      ModuleId.CanvasRender,
      ModuleId.CanvasFission,
      // 自由画布-智能模型（module 35，ComfyUI 模板）：模板产物不支持发布（与收藏/参考图黑名单口径一致）
      ModuleId.CanvasComfyUIMulti,
      ModuleId.GptImage2,
      ModuleId.GptImageV35,
      ModuleId.GptImageV35Flare,
      ModuleId.GptImage2Enterprise,
      ModuleId.CanvasDraft,
      MODULE_ID_CANVAS_DRAFT_A3,
      ModuleId.CanvasFission,
      ModuleId.CanvasLocalRefine,
      ModuleId.CanvasSeedreamV5Pro,
      // 悠船 Midjourney（旧通道 + 阿里云）
      ModuleId.Midjourney,
      ModuleId.MidjourneyV81,
      ModuleId.MidjourneyNiji7,
      ModuleId.MidjourneyV82,
      ModuleId.AliyunYouchuan,
      ModuleId.AliyunYouchuanV81,
      ModuleId.AliyunYouchuanNiji7,
      ModuleId.AliyunYouchuanV82Fastest,
      ...CANVAS_VIDEO_MODULES
    ];

export function isCanvasPublishModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  const id = Number(moduleId);
  if (!Number.isFinite(id) || id <= 0) return false;
  return CANVAS_PUBLISH_MODULE_ID_WHITELIST.includes(id);
}

/**
 * 需隐藏收藏按钮的模块（与 ImageActions / ImageHoverOverlay 对齐）
 * PSD、Comfy 单/多图拆分、混元 3D、全能拆分暂不支持收藏
 */
export const FAVORITE_HIDDEN_MODULE_IDS: number[] = [
  ModuleId.CanvasComfyUIPSD,
  ModuleId.CanvasComfyUIMulti,
  ModuleId.CanvasComfyUISingle,
  ModuleId.CanvasGenerate3d,
  ModuleId.GameUiSplit
];

/** 该 module 是否应隐藏收藏按钮 */
export function isFavoriteHiddenByModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  const id = Number(moduleId);
  if (!Number.isFinite(id)) return false;
  return FAVORITE_HIDDEN_MODULE_IDS.includes(id);
}

/**
 * 在基础 showFavorite 之上叠加 module 黑名单
 * @param base 来自 creationType / props 的原始显隐
 */
export function resolveShowFavorite(base: boolean, moduleId?: number | null): boolean {
  return base && !isFavoriteHiddenByModule(moduleId);
}

/**
 * 需隐藏「作为参考图」的模块（与 ImageHoverOverlay 对齐）
 * 混元 3D（自由画布-3D生成）预览图、全能拆分结果不可作为参考图；
 * 智能模型（module 35，ComfyUI 模板）为模板产物，不支持作为参考图（与详情二级操作区隐藏口径一致）
 */
export const USE_AS_REF_HIDDEN_MODULE_IDS: number[] = [
  ModuleId.CanvasGenerate3d,
  ModuleId.CanvasComfyUISingle,
  ModuleId.GameUiSplit
];

/** 该 module 是否应隐藏「作为参考图」按钮 */
export function isUseAsRefHiddenByModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  const id = Number(moduleId);
  if (!Number.isFinite(id)) return false;
  return USE_AS_REF_HIDDEN_MODULE_IDS.includes(id);
}

/**
 * 在基础 showUseAsRef / showReference 之上叠加 module 黑名单
 * @param base 来自 creationType / props 的原始显隐
 */
export function resolveShowUseAsRef(base: boolean, moduleId?: number | null): boolean {
  return base && !isUseAsRefHiddenByModule(moduleId);
}

/**
 * 需隐藏「下载」按钮的模块（与 ImageHoverOverlay 对齐）
 * 全能拆分结果（module 87）为拆分层级产物，不支持单图下载
 */
export const DOWNLOAD_HIDDEN_MODULE_IDS: number[] = [ModuleId.GameUiSplit];

/** 该 module 是否应隐藏「下载」按钮 */
export function isDownloadHiddenByModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  const id = Number(moduleId);
  if (!Number.isFinite(id)) return false;
  return DOWNLOAD_HIDDEN_MODULE_IDS.includes(id);
}

/**
 * 在基础 showDownload 之上叠加 module 黑名单
 * @param base 来自 creationType / props 的原始显隐
 */
export function resolveShowDownload(base: boolean, moduleId?: number | null): boolean {
  return base && !isDownloadHiddenByModule(moduleId);
}

/** 判断是否为万能渲染模块（CanvasRender） */
export function isCanvasRenderModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  return Number(moduleId) === ModuleId.CanvasRender;
}

/** 判断是否为 AI 创作一键成稿族（Explore / ExploreA3） */
export function isExploreFamilyModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  return (EXPLORE_FAMILY_IDS as readonly number[]).includes(Number(moduleId));
}

/** 判断是否为自由画布一键成稿族（CanvasDraft / CanvasDraftA3） */
export function isCanvasDraftModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  return (CANVAS_DRAFT_FAMILY_IDS as readonly number[]).includes(Number(moduleId));
}

/** 判断是否为自由画布相似图裂变模块（CanvasFission） */
export function isCanvasFissionModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  return Number(moduleId) === ModuleId.CanvasFission;
}

/** 判断是否为自由画布局部修改模块（CanvasLocalRefine） */
export function isCanvasLocalRefineModule(moduleId: number | undefined | null): boolean {
  if (moduleId == null) return false;
  return Number(moduleId) === ModuleId.CanvasLocalRefine;
}

/**
 * 智能扩图是否使用V2版本
 */
export const MODULE_ID_CANVAS_QWEN_FREE_PERSPECTIVE = ModuleId.CanvasQwenFreePerspective as ModuleIds;
export const IS_EXPANSION_V2_ENABLED = true;
